import { Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Check, Star, Zap, Crown, ArrowRight } from 'lucide-react';

const PricingPage = () => {
  const guestFeatures = [
    'Kostenlose Registrierung',
    'Unbegrenzt Hochzeiten durchsuchen',
    'Bewerbungen senden',
    'Direkter Kontakt nach Buchung',
    'Bewertungen abgeben',
  ];

  const premiumGuestFeatures = [
    'Alles aus Basic',
    'Priorität bei Bewerbungen',
    'Vorrang bei beliebten Events',
    'Exklusive Hochzeiten sehen',
    'Premium Support',
    'Verifizierungsbadge inklusive',
  ];

  const hostFeatures = [
    'Kostenlose Registrierung',
    'Hochzeit eintragen',
    'Bewerbungen verwalten',
    'Direkter Kontakt mit Gästen',
    'Sichere Zahlungsabwicklung',
    '85% der Einnahmen behalten',
  ];

  const addOns = [
    {
      icon: Star,
      name: 'Verifizierungsbadge',
      price: '9,99€',
      period: 'einmalig',
      description: 'Zeigen Sie anderen, dass Ihre Identität geprüft wurde.',
    },
    {
      icon: Zap,
      name: 'Featured Listing',
      price: '29€',
      period: 'pro Hochzeit',
      description: 'Ihre Hochzeit erscheint prominent auf der Startseite.',
    },
  ];

  return (
    <Layout>
      <div className="min-h-screen" data-testid="pricing-page">
        {/* Hero */}
        <section className="bg-primary py-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h1 className="font-serif text-4xl md:text-5xl font-bold text-white mb-4">
              Einfache, transparente Preise
            </h1>
            <p className="text-white/90 text-lg">
              Starten Sie kostenlos. Zahlen Sie nur bei erfolgreicher Vermittlung.
            </p>
          </div>
        </section>

        {/* For Guests */}
        <section className="py-20 bg-secondary">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl font-bold mb-4">Für Gäste</h2>
              <p className="text-muted-foreground">
                Wählen Sie das Paket, das zu Ihnen passt
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {/* Basic Guest */}
              <Card className="relative">
                <CardHeader className="text-center pb-2">
                  <CardTitle className="font-serif text-2xl">Basic</CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold">Kostenlos</span>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <ul className="space-y-3 mb-8">
                    {guestFeatures.map((feature, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button asChild className="w-full rounded-full" variant="outline">
                    <Link to="/register?role=guest">Kostenlos starten</Link>
                  </Button>
                  <p className="text-xs text-muted-foreground text-center mt-4">
                    + Beitrag pro gebuchter Hochzeit
                  </p>
                </CardContent>
              </Card>

              {/* Premium Guest */}
              <Card className="relative border-primary shadow-lg">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-primary text-white px-4 py-1">
                    <Crown className="h-3 w-3 mr-1" />
                    BELIEBT
                  </Badge>
                </div>
                <CardHeader className="text-center pb-2">
                  <CardTitle className="font-serif text-2xl">Premium</CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold">14,99€</span>
                    <span className="text-muted-foreground">/Monat</span>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <ul className="space-y-3 mb-8">
                    {premiumGuestFeatures.map((feature, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button asChild className="w-full rounded-full">
                    <Link to="/register?role=guest">Premium starten</Link>
                  </Button>
                  <p className="text-xs text-muted-foreground text-center mt-4">
                    Jederzeit kündbar
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* For Hosts */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl font-bold mb-4">Für Gastgeber</h2>
              <p className="text-muted-foreground">
                Keine Vorabkosten. Zahlen Sie nur bei erfolgreicher Buchung.
              </p>
            </div>

            <Card className="max-w-xl mx-auto">
              <CardHeader className="text-center pb-2">
                <CardTitle className="font-serif text-2xl">Gastgeber</CardTitle>
                <div className="mt-4">
                  <span className="text-4xl font-bold">Kostenlos</span>
                  <p className="text-muted-foreground mt-2">
                    15% Provision auf erfolgreiche Buchungen
                  </p>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <ul className="space-y-3 mb-8">
                  {hostFeatures.map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                      <span className="text-muted-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button asChild className="w-full rounded-full">
                  <Link to="/register?role=host">Gastgeber werden</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Commission Explanation */}
            <div className="mt-12 max-w-2xl mx-auto">
              <Card className="bg-primary/5 border-primary/20">
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-4">Beispielrechnung</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Preis pro Gast:</span>
                      <span className="font-medium">150€</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Anzahl Gäste:</span>
                      <span className="font-medium">10</span>
                    </div>
                    <div className="flex justify-between border-t pt-2 mt-2">
                      <span>Gesamteinnahmen:</span>
                      <span className="font-medium">1.500€</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>WEDVAYA Provision (15%):</span>
                      <span>-225€</span>
                    </div>
                    <div className="flex justify-between border-t pt-2 mt-2 text-lg">
                      <span className="font-semibold">Sie erhalten:</span>
                      <span className="font-bold text-primary">1.275€</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Add-ons */}
        <section className="py-20 bg-secondary">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl font-bold mb-4">Zusätzliche Features</h2>
              <p className="text-muted-foreground">
                Optionale Erweiterungen für mehr Sichtbarkeit
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
              {addOns.map((addon, index) => (
                <Card key={index}>
                  <CardContent className="p-6 flex gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <addon.icon className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">{addon.name}</h3>
                      <p className="text-2xl font-bold text-primary">
                        {addon.price}
                        <span className="text-sm font-normal text-muted-foreground ml-1">
                          {addon.period}
                        </span>
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        {addon.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-primary">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="font-serif text-3xl font-bold text-white mb-4">
              Bereit loszulegen?
            </h2>
            <p className="text-white/90 mb-8">
              Registrieren Sie sich kostenlos und entdecken Sie WEDVAYA.
            </p>
            <Button
              size="lg"
              className="bg-white text-primary hover:bg-white/90 rounded-full px-8"
              asChild
            >
              <Link to="/register">
                Jetzt kostenlos starten
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default PricingPage;
